import { useEffect, useMemo, useRef, useState } from "react";
import { MultiChip } from "../../lib/funnel/filters";
import { groupCountry, monthLabel, recentMonths } from "../../lib/funnel/helpers";
import { PRIMARY, GOLD, FG1, FG3 } from "../../lib/funnel/theme";
import type { BaselineRow, CohortMode } from "./sql";
import { STAGES, newTimingId, passesTimings, describeTiming } from "./stages";
import type { StageTiming, StageKey, DayOp } from "./stages";

export type SignupSource = "all" | "first_company" | "team_invite";

const SIGNUP_OPTIONS: { value: SignupSource; label: string }[] = [
  { value: "all",           label: "All" },
  { value: "first_company", label: "First company sign-up" },
  { value: "team_invite",   label: "Team invite" },
];

const COHORT_MODE_OPTIONS: { value: CohortMode; label: string }[] = [
  { value: "baseline",        label: "Agreed baseline cohort" },
  { value: "all_users",       label: "All users" },
  { value: "demo",            label: "Demo'd companies" },
  { value: "support_no_demo", label: "Support role, no demo" },
  { value: "support_demo",    label: "Support role, demo'd" },
];

export type Filters = {
  cohortMode: CohortMode;
  signupSource: SignupSource;
  regions: string[];        // groupCountry() buckets: "AU" | "NZ" | "GB" | "Rest of World"
  companySizes: string[];   // "1" | "2-5" | "6-10" | "10+"
  lookbackDays: number;     // 90 | 180 | 270 | 360 — trailing window length (ends at now-30d)
  months: string[];         // YYYY-MM signup months; empty = no month filter
  stageTimings: StageTiming[]; // empty = no stage-timing filter
};

export type FiltersBarTone = "baseline" | "comparison";

const LOOKBACK_OPTIONS = [
  { value: 90,  label: "Last 90 days" },
  { value: 180, label: "Last 180 days" },
  { value: 270, label: "Last 270 days" },
  { value: 360, label: "Last 360 days" },
];

export function defaultFilters(): Filters {
  return { cohortMode: "baseline", signupSource: "all", regions: [], companySizes: [], lookbackDays: 180, months: [], stageTimings: [] };
}

export function defaultComparisonFilters(): Filters {
  return { cohortMode: "all_users", signupSource: "all", regions: [], companySizes: [], lookbackDays: 180, months: [], stageTimings: [] };
}

export const REGION_OPTIONS = [
  { value: "AU", label: "🇦🇺 AU" },
  { value: "NZ", label: "🇳🇿 NZ" },
  { value: "GB", label: "🇬🇧 UK" },
  { value: "Rest of World", label: "🌍 ROW" },
];

export const COMPANY_SIZE_OPTIONS = [
  { value: "1",    label: "1 user" },
  { value: "2-5",  label: "2-5 users" },
  { value: "6-10", label: "6-10 users" },
  { value: "10+",  label: "10+ users" },
];

export function companySizeBucket(n: number): string {
  if (n <= 1) return "1";
  if (n <= 5) return "2-5";
  if (n <= 10) return "6-10";
  return "10+";
}

// US gets folded into 'Rest of World' to match the four-way region split.
function regionBucket(code: string | null): string {
  const g = groupCountry(code);
  if (g === "AU" || g === "NZ" || g === "GB") return g;
  return "Rest of World";
}

export function applyFilters(rows: BaselineRow[], f: Filters): BaselineRow[] {
  const regionSet = new Set(f.regions);
  const sizeSet = new Set(f.companySizes);
  const monthSet = new Set(f.months);

  // Lookback window: trailing N days. Baseline mode anchors the end at
  // now()-30d (matches the SQL exclusion); all-users mode runs all the way to now().
  const endTs = f.cohortMode === "all_users" ? Date.now() : Date.now() - 30 * 86400_000;
  const startTs = Date.now() - f.lookbackDays * 86400_000;

  return rows.filter((r) => {
    if (f.signupSource === "first_company" && r.is_team_invite) return false;
    if (f.signupSource === "team_invite" && !r.is_team_invite) return false;
    if (regionSet.size > 0 && !regionSet.has(regionBucket(r.country_code))) return false;
    if (sizeSet.size > 0 && !sizeSet.has(companySizeBucket(r.marloo_active_user_count))) return false;

    const signupTs = new Date(r.signup_date_utc).getTime();
    if (Number.isNaN(signupTs)) return false;
    if (signupTs < startTs || signupTs >= endTs) return false;

    if (monthSet.size > 0) {
      const ym = r.signup_date_utc.slice(0, 7); // YYYY-MM
      if (!monthSet.has(ym)) return false;
    }
    if (f.stageTimings.length > 0 && !passesTimings(r, f.stageTimings)) return false;
    return true;
  });
}

// Single-select chip that mirrors MultiChip's popover styling. Uses the same
// .filter-chip / .month-multi-popover classes so the visual stays consistent
// with the rest of the filter bar.
function SingleSelectChip<T extends string | number>({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T;
  onChange: (next: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const display = options.find((o) => o.value === selected)?.label ?? String(selected);

  return (
    <span className="filter-chip month-multi" ref={ref}>
      <span className="chip-label">{label}:</span>
      <span className="chip-value">{display}</span>
      <i className="ti ti-chevron-down chevron" />
      <button
        type="button"
        className="chip-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${display}`}
      />
      {open && (
        <div className="month-multi-popover" role="listbox" onClick={(e) => e.stopPropagation()}>
          {options.map((o) => (
            <label
              key={o.value}
              className="row"
              onClick={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); }}
            >
              <input
                type="radio"
                checked={selected === o.value}
                onChange={() => {}}
                tabIndex={-1}
                style={{ pointerEvents: "none" }}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </span>
  );
}

export function FiltersBar({
  filters,
  onChange,
  cohortSize,
  tone = "baseline",
  headerLabel,
  headerExtra,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  cohortSize: number;
  tone?: FiltersBarTone;
  headerLabel?: string;
  headerExtra?: React.ReactNode;
}) {
  const accent = tone === "comparison" ? GOLD : PRIMARY;
  // Months within the chosen lookback window (excluding the trailing 30 days).
  // We compute relative to today and trim to whatever the lookback covers.
  const monthOptions = useMemo(() => {
    const all = recentMonths();
    const endMs = filters.cohortMode === "all_users" ? Date.now() : Date.now() - 30 * 86400_000;
    const startMs = Date.now() - filters.lookbackDays * 86400_000;
    return all
      .filter((ym) => {
        const [y, m] = ym.split("-").map(Number) as [number, number];
        const monthStart = Date.UTC(y, m - 1, 1);
        const monthEnd = Date.UTC(y, m, 1) - 1;
        return monthEnd >= startMs && monthStart <= endMs;
      })
      .map((ym) => ({ value: ym, label: monthLabel(ym) }));
  }, [filters.lookbackDays, filters.cohortMode]);

  // If the lookback shrinks past selected months, drop them silently so the
  // filter never traps the page in an empty state the user can't see.
  useEffect(() => {
    if (filters.months.length === 0) return;
    const valid = new Set(monthOptions.map((o) => o.value));
    const pruned = filters.months.filter((m) => valid.has(m));
    if (pruned.length !== filters.months.length) {
      onChange({ ...filters, months: pruned });
    }
  }, [monthOptions, filters, onChange]);

  return (
    <div
      className="filterbar"
      style={{
        marginBottom: 12,
        borderLeft: `3px solid ${accent}`,
        paddingLeft: 12,
        flexWrap: "wrap",
        rowGap: 8,
        columnGap: 8,
        alignItems: "center",
      }}
    >
      {headerLabel && (
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
          textTransform: "uppercase", color: accent,
          paddingRight: 4, whiteSpace: "nowrap",
        }}>
          <span style={{
            display: "inline-block", width: 7, height: 7, borderRadius: 2,
            background: accent,
          }} />
          {headerLabel}
        </span>
      )}
      <SingleSelectChip<CohortMode>
        label="Cohort"
        options={COHORT_MODE_OPTIONS}
        selected={filters.cohortMode}
        onChange={(next) => onChange({ ...filters, cohortMode: next })}
      />
      <SingleSelectChip<number>
        label="Lookback"
        options={LOOKBACK_OPTIONS}
        selected={filters.lookbackDays}
        onChange={(next) => onChange({ ...filters, lookbackDays: next })}
      />

      <MultiChip
        label="Sign-up month"
        allLabel="All months"
        pluralNoun="months"
        options={monthOptions}
        selected={filters.months}
        onChange={(next) => onChange({ ...filters, months: next })}
      />

      <SingleSelectChip<SignupSource>
        label="Signup source"
        options={SIGNUP_OPTIONS}
        selected={filters.signupSource}
        onChange={(next) => onChange({ ...filters, signupSource: next })}
      />

      <MultiChip
        label="Region"
        allLabel="All regions"
        pluralNoun="regions"
        options={REGION_OPTIONS}
        selected={filters.regions}
        onChange={(next) => onChange({ ...filters, regions: next })}
      />

      <MultiChip
        label="Company size"
        allLabel="All sizes"
        pluralNoun="sizes"
        options={COMPANY_SIZE_OPTIONS}
        selected={filters.companySizes}
        onChange={(next) => onChange({ ...filters, companySizes: next })}
      />

      <StageTimingsControl
        timings={filters.stageTimings}
        onChange={(next) => onChange({ ...filters, stageTimings: next })}
      />

      <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 12, color: accent, fontWeight: 600 }}>
          {cohortSize.toLocaleString()} users in view
        </span>
        {headerExtra}
      </span>
    </div>
  );
}

const OP_LABEL: Record<DayOp, string> = {
  within: "within",
  after:  "after",
  ever:   "ever",
  never:  "never",
};

function PopoverChip({
  label,
  display,
  width,
  children,
}: {
  label: string;
  display: string;
  width?: number;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);
  // Wrapper is NOT .filter-chip so the global `.filter-chip select` rules
  // don't bleed into popover-rendered selects/inputs.
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <span className="filter-chip" style={{ position: "relative" }}>
        <span className="chip-label">{label}:</span>
        <span className="chip-value">{display}</span>
        <i className="ti ti-chevron-down chevron" />
        <button
          type="button"
          className="chip-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`${label}: ${display}`}
        />
      </span>
      {open && (
        <div
          className="month-multi-popover"
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: width ?? 240, padding: 8 }}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </span>
  );
}

function StageTimingsControl({
  timings,
  onChange,
}: {
  timings: StageTiming[];
  onChange: (next: StageTiming[]) => void;
}) {
  const updateAt = (i: number, patch: Partial<StageTiming>) => {
    onChange(timings.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  };
  const removeAt = (i: number) => onChange(timings.filter((_, idx) => idx !== i));
  const add = () => {
    onChange([...timings, { id: newTimingId(), stage: "calendar", op: "within", days: 1 }]);
  };

  return (
    <>
      {timings.map((t, i) => (
        <TimingChip
          key={t.id}
          timing={t}
          onChange={(patch) => updateAt(i, patch)}
          onRemove={() => removeAt(i)}
        />
      ))}
      <button
        type="button"
        onClick={add}
        title="Add a constraint"
        style={{
          fontSize: 11,
          padding: "3px 8px",
          border: "1px dashed var(--border-default)",
          borderRadius: 6,
          background: "transparent",
          color: FG1,
          cursor: "pointer",
        }}
      >
        + Constraint
      </button>
    </>
  );
}

function TimingChip({
  timing,
  onChange,
  onRemove,
}: {
  timing: StageTiming;
  onChange: (patch: Partial<StageTiming>) => void;
  onRemove: () => void;
}) {
  return (
    <PopoverChip label="Constraint" display={describeTiming(timing)} width={300}>
      {() => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, color: FG3, marginBottom: 4 }}>Stage</div>
            <select
              value={timing.stage}
              onChange={(e) => onChange({ stage: e.target.value as StageKey })}
              style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid var(--border-default)", borderRadius: 4 }}
            >
              {STAGES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: FG3, marginBottom: 4 }}>Constraint</div>
            <div style={{ display: "flex", gap: 4 }}>
              {(["within", "after", "ever", "never"] as const).map((op) => (
                <button
                  key={op}
                  type="button"
                  onClick={() => onChange({ op })}
                  style={{
                    flex: 1,
                    padding: "4px 6px",
                    fontSize: 11,
                    border: "1px solid var(--border-default)",
                    borderRadius: 4,
                    background: timing.op === op ? PRIMARY : "transparent",
                    color: timing.op === op ? "#fff" : FG1,
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {OP_LABEL[op]}
                </button>
              ))}
            </div>
          </div>
          {timing.op !== "never" && timing.op !== "ever" && (
            <div>
              <div style={{ fontSize: 11, color: FG3, marginBottom: 4 }}>Days since signup</div>
              <input
                type="number"
                min={0}
                value={timing.days}
                onChange={(e) => onChange({ days: Math.max(0, Number(e.target.value) || 0) })}
                style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid var(--border-default)", borderRadius: 4 }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={onRemove}
            style={{
              fontSize: 11,
              padding: "4px 6px",
              border: "1px solid var(--border-default)",
              borderRadius: 4,
              background: "transparent",
              color: "var(--fg-danger, #B45A3F)",
              cursor: "pointer",
            }}
          >
            Remove constraint
          </button>
        </div>
      )}
    </PopoverChip>
  );
}
